import React, { useState, useEffect, useRef } from "react";
import "./TimePicker.css";

export const TimePicker = ({ value, onChange, variant = 'input' }) => {
    const totalSeconds = value || 0;
    const [hours, setHours] = useState(String(Math.floor(totalSeconds / 3600)));
    const [minutes, setMinutes] = useState(String(Math.floor((totalSeconds % 3600) / 60)));
    const [seconds, setSeconds] = useState(String(totalSeconds % 60));
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const pickerRef = useRef(null);

    useEffect(() => {
        const nextValue = value || 0;
        setHours(String(Math.floor(nextValue / 3600)));
        setMinutes(String(Math.floor((nextValue % 3600) / 60)));
        setSeconds(String(nextValue % 60));
    }, [value]);

    useEffect(() => {
        if (!isPickerOpen) return;

        const closePicker = (event) => {
            if (!pickerRef.current?.contains(event.target)) setIsPickerOpen(false);
        };
        const closePickerOnEscape = (event) => {
            if (event.key === 'Escape') setIsPickerOpen(false);
        };

        document.addEventListener('mousedown', closePicker);
        document.addEventListener('keydown', closePickerOnEscape);
        return () => {
            document.removeEventListener('mousedown', closePicker);
            document.removeEventListener('keydown', closePickerOnEscape);
        };
    }, [isPickerOpen]);

    const getDurationInSeconds = (nextHours = hours, nextMinutes = minutes, nextSeconds = seconds) => (
        (parseInt(nextHours, 10) || 0) * 3600
        + (parseInt(nextMinutes, 10) || 0) * 60
        + (parseInt(nextSeconds, 10) || 0)
    );

    const handleHoursChange = (e) => {
        const nextHours = e.target.value;
        if (!/^\d*$/.test(nextHours)) return;

        setHours(nextHours);
        if (nextHours !== '') onChange(getDurationInSeconds(nextHours));
    };

    const handleMinutesChange = (e) => {
        const nextMinutes = e.target.value;
        if (!/^\d*$/.test(nextMinutes)) return;

        setMinutes(nextMinutes);
        if (nextMinutes !== '') {
            onChange(getDurationInSeconds(hours, nextMinutes));
        }
    };

    const handleSecondsChange = (e) => {
        const nextSeconds = e.target.value;
        if (!/^\d*$/.test(nextSeconds)) return;

        const secs = Math.min(59, parseInt(nextSeconds, 10));
        setSeconds(nextSeconds === '' ? '' : String(secs));
        if (nextSeconds !== '') {
            onChange(getDurationInSeconds(hours, minutes, secs));
        }
    };

    const handleHoursBlur = () => {
        const clamped = Math.min(23, parseInt(hours, 10) || 0);
        setHours(String(clamped));
        onChange(getDurationInSeconds(clamped));
    };

    const handleMinutesBlur = () => {
        const mins = Math.min(59, parseInt(minutes, 10) || 0);
        setMinutes(String(mins));
        onChange(getDurationInSeconds(hours, mins));
    };

    const handleSecondsBlur = () => {
        const clamped = Math.min(59, parseInt(seconds, 10) || 0);
        setSeconds(String(clamped));
        onChange(getDurationInSeconds(hours, minutes, clamped));
    };

    const handleHourSelect = (e) => {
        const nextHours = parseInt(e.target.value, 10);
        setHours(String(nextHours));
        onChange(getDurationInSeconds(nextHours));
    };

    const handleMinuteSelect = (e) => {
        const mins = parseInt(e.target.value, 10);
        setMinutes(String(mins));
        onChange(getDurationInSeconds(hours, mins));
    };

    const handleSecondSelect = (e) => {
        const secs = parseInt(e.target.value, 10);
        setSeconds(String(secs));
        onChange(getDurationInSeconds(hours, minutes, secs));
    };

    if (variant === 'popover') {
        const hourValue = parseInt(hours, 10) || 0;
        const minuteValue = parseInt(minutes, 10) || 0;
        const secondValue = parseInt(seconds, 10) || 0;

        return (
            <div className="duration-picker" ref={pickerRef}>
                <button
                    type="button"
                    className="duration-picker-trigger"
                    onClick={() => setIsPickerOpen((open) => !open)}
                    aria-label={`Duration ${hourValue} hours ${minuteValue} minutes ${secondValue} seconds`}
                    aria-expanded={isPickerOpen}
                >
                    <span>{String(hourValue).padStart(2, '0')}:{String(minuteValue).padStart(2, '0')}:{String(secondValue).padStart(2, '0')}</span>
                    <span className="duration-picker-chevron" aria-hidden="true" />
                </button>
                {isPickerOpen && (
                    <div className="duration-picker-popover">
                        <label>
                            <span>Hours</span>
                            <select value={hours} onChange={handleHourSelect} aria-label="Hours">
                                {Array.from({ length: 24 }, (_, hour) => (
                                    <option key={hour} value={hour}>{String(hour).padStart(2, '0')}</option>
                                ))}
                            </select>
                        </label>
                        <span className="duration-picker-divider">:</span>
                        <label>
                            <span>Minutes</span>
                            <select value={minutes} onChange={handleMinuteSelect} aria-label="Minutes">
                                {Array.from({ length: 60 }, (_, minute) => (
                                    <option key={minute} value={minute}>{String(minute).padStart(2, '0')}</option>
                                ))}
                            </select>
                        </label>
                        <span className="duration-picker-divider">:</span>
                        <label>
                            <span>Seconds</span>
                            <select value={seconds} onChange={handleSecondSelect} aria-label="Seconds">
                                {Array.from({ length: 60 }, (_, second) => (
                                    <option key={second} value={second}>{String(second).padStart(2, '0')}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                )}
            </div>
        );
    }

    if (variant === 'select') {
        return (
            <div className="time-picker time-picker-select">
                <select value={hours} onChange={handleHourSelect} aria-label="Hours">
                    {Array.from({ length: 24 }, (_, hour) => (
                        <option key={hour} value={hour}>{String(hour).padStart(2, '0')} hr</option>
                    ))}
                </select>
                <select value={minutes} onChange={handleMinuteSelect} aria-label="Minutes">
                    {Array.from({ length: 60 }, (_, minute) => (
                        <option key={minute} value={minute}>{String(minute).padStart(2, '0')} min</option>
                    ))}
                </select>
                <select value={seconds} onChange={handleSecondSelect} aria-label="Seconds">
                    {Array.from({ length: 60 }, (_, second) => (
                        <option key={second} value={second}>{String(second).padStart(2, '0')} sec</option>
                    ))}
                </select>
            </div>
        );
    }

    return (
        <div className="time-picker">
            <div className="time-picker-field">
                <input
                    type="number"
                    value={hours}
                    onChange={handleHoursChange}
                    onBlur={handleHoursBlur}
                    min={0}
                    max={23}
                    className="time-picker-input"
                    aria-label="Hours"
                />
                <span className="time-picker-label">hr</span>
            </div>
            <span className="time-picker-separator">:</span>
            <div className="time-picker-field">
                <input
                    type="number"
                    value={minutes}
                    onChange={handleMinutesChange}
                    onBlur={handleMinutesBlur}
                    min={0}
                    max={59}
                    className="time-picker-input"
                    aria-label="Minutes"
                />
                <span className="time-picker-label">min</span>
            </div>
            <span className="time-picker-separator">:</span>
            <div className="time-picker-field">
                <input
                    type="number"
                    value={seconds}
                    onChange={handleSecondsChange}
                    onBlur={handleSecondsBlur}
                    min={0}
                    max={59}
                    className="time-picker-input"
                    aria-label="Seconds"
                />
                <span className="time-picker-label">sec</span>
            </div>
        </div>
    );
};
